import {
  ArrowRed,
  BaseChartDataItem,
  CustomExpenseInRangeEntity,
  FinancesExpensesBreakdown,
  GroupByType,
  Icon,
  lightTheme,
} from '@vgrubs/components';
import { useState } from 'react';
import { ExpensesManagerModal } from './ExpensesManagerModal';
import { BreakdownItem, FinanceTabbedCard } from './FinanceTabbedCard';

// Cycling palette for custom expense slices without an explicit pieColor.
// Stays clear of the hardcoded colors used by the API-driven slices below.
const CUSTOM_COLORS = [
  lightTheme.darkBlue,
  lightTheme.green,
  lightTheme.darkYellow,
  lightTheme.darkGray,
  lightTheme.black,
];

/**
 * Operating Expenses - Pie/Graph tabbed card (Sales Commissions, vDrive VR,
 * Ad Spend per provider, Esper, + any admin-managed custom expenses).
 *
 * Ad spend is one line per provider (`adSpend` is UberEats only, `adSpendGrubhub`
 * is Grubhub) and either can be negative when that provider's ad credits outran
 * its charges - a real refund, so it is never clamped to zero.
 *
 * Custom expenses come from the backend already computed for the active range
 * (`customExpenses` on the /v1/finances response); their sum is layered on top
 * of the API expense totals so the pie + headline reflect them. Create/delete
 * happens in the ExpensesManagerModal, which calls `onExpensesChanged` to
 * trigger a refetch.
 */
export const OperatingExpensesCard = ({
  expensesSeries,
  expensesSeriesCompared,
  totalExpensesSum,
  totalExpensesSumToCompare,
  groupByType,
  isComparing,
  expensesBreakdown,
  customExpenses,
  onExpensesChanged,
  periodLabel,
}: {
  expensesSeries: BaseChartDataItem[];
  expensesSeriesCompared: BaseChartDataItem[];
  totalExpensesSum: number;
  totalExpensesSumToCompare: number;
  groupByType: GroupByType;
  isComparing: boolean;
  expensesBreakdown: FinancesExpensesBreakdown | null | undefined;
  /** Range-computed custom expenses from the /v1/finances response. */
  customExpenses: CustomExpenseInRangeEntity[];
  /** Called after a create/delete so the parent can refetch the overview. */
  onExpensesChanged: () => void;
  /** Active date range, shown under the headline. */
  periodLabel?: string;
}) => {
  const [managerOpen, setManagerOpen] = useState(false);

  // Backend already returns each expense's contribution for the active range.
  const customSliceItems: BreakdownItem[] = customExpenses.map((exp, i) => ({
    label: exp.name,
    amount: exp.amount,
    // Prefer the admin-chosen color; fall back to the cycling palette.
    color: exp.pieColor || CUSTOM_COLORS[i % CUSTOM_COLORS.length],
  }));

  const breakdownItems: BreakdownItem[] = [
    {
      label: 'Sales Commissions',
      amount: expensesBreakdown?.salesCommission ?? 0,
    },
    { label: 'vDrive VR', amount: expensesBreakdown?.vDriveCost ?? 0 },
    {
      label: 'Ad Spend (Uber)',
      amount: expensesBreakdown?.adSpend ?? 0,
      color: lightTheme.orange,
    },
    {
      label: 'Ad Spend (Grubhub)',
      amount: expensesBreakdown?.adSpendGrubhub ?? 0,
      color: lightTheme.chipYellow,
    },
    {
      label: 'Esper',
      amount: expensesBreakdown?.esper ?? 0,
      color: lightTheme.blue,
    },
    {
      label: 'FoodHub',
      amount: expensesBreakdown?.foodHub ?? 0,
      color: lightTheme.green,
    },
    {
      label: 'KitchenHub',
      amount: expensesBreakdown?.kitchenHub ?? 0,
      color: lightTheme.darkBlue,
    },
    ...customSliceItems,
    // {
    //   label: 'Custom',
    //   amount: expensesBreakdown?.custom ?? 0,
    //   color: lightTheme.yellow,
    // },
  ];

  return (
    <>
      <FinanceTabbedCard
        title="Operating Expenses"
        headlineCents={totalExpensesSum}
        headlineCompareCents={totalExpensesSumToCompare}
        negateDisplay
        valueColor={lightTheme.primary}
        series={expensesSeries}
        seriesCompared={expensesSeriesCompared}
        seriesKey="expenses"
        tooltipLabel="Operating Expenses"
        breakdownItems={breakdownItems}
        breakdownAmountLabel="Expenses"
        breakdownRanked
        periodLabel={periodLabel}
        groupByType={groupByType}
        isComparing={isComparing}
        headerExtra={
          <Icon
            $pointer
            onClick={() => setManagerOpen(true)}
            color={lightTheme.lightGray}
          >
            <ArrowRed />
          </Icon>
        }
      />

      <ExpensesManagerModal
        isOpen={managerOpen}
        onClose={() => setManagerOpen(false)}
        onChanged={onExpensesChanged}
      />
    </>
  );
};
